/**
 * As mensagens que o canal manda sozinho (H19).
 *
 * ── Duas, e não um editor de automações ───────────────────────────────────
 *
 * Saudação e fora de expediente são as únicas automáticas seguras no WhatsApp:
 * as duas RESPONDEM a uma mensagem do cliente, então caem sempre dentro da
 * janela de 24 h da Meta. Regra por gatilho e campanha caem fora dela, e fora
 * dela só passa template aprovado — que leva até um dia para ser revisado.
 *
 * Oferecer aqui um campo de texto livre para aquelas seria oferecer uma
 * mensagem que a Meta recusa depois, com a pessoa achando que mandou.
 *
 * ── Por canal, e dentro do canal ──────────────────────────────────────────
 *
 * Cada número tem as suas: a revenda com dois números costuma ter um de vendas
 * e um de oficina, e a saudação dos dois não é a mesma. Uma seção separada
 * obrigaria a escolher o número num seletor, longe do lugar onde ele aparece.
 *
 * ── Fechado por padrão ────────────────────────────────────────────────────
 *
 * A lista de canais responde "qual número está ligado?", que é a pergunta de
 * quem abre Conexões. Dois editores de texto abertos por número empurrariam
 * essa resposta para fora da tela.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CanalDeWhatsApp, MensagemAutomatica } from '@cliqcrm/contracts';
import { Botao, CampoArea, Selo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { useApi } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import './mensagens-automaticas.css';

/** O teto do motor, repetido aqui para a recusa acontecer ANTES do envio. */
const MAXIMO = 10_000;

type Qual = 'saudacao' | 'fora_de_expediente';

export function MensagensAutomaticas({ canal }: { canal: CanalDeWhatsApp }): ReactNode {
  return (
    <div className="auto__corpo">
      <Bloco
        canal={canal}
        qual="saudacao"
        titulo="Saudação"
        explicacao="Sai na PRIMEIRA mensagem de cada contato, uma vez por conversa. É a resposta que chega enquanto ninguém da equipe abriu a conversa ainda."
        exemplo="Oi! Recebemos sua mensagem e já vamos te atender."
      />
      <Bloco
        canal={canal}
        qual="fora_de_expediente"
        titulo="Fora de expediente"
        explicacao="Sai quando a mensagem chega fora do horário de atendimento, uma vez por dia. O horário é o que está em Configurações → Atendimento, e não um segundo horário configurado aqui."
        exemplo="Estamos fechados agora. Respondemos assim que abrirmos, às 9h."
      />
    </div>
  );
}

function Bloco({
  canal,
  qual,
  titulo,
  explicacao,
  exemplo,
}: {
  canal: CanalDeWhatsApp;
  qual: Qual;
  titulo: string;
  explicacao: string;
  exemplo: string;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const atual = canal.mensagens_automaticas[qual];

  const [texto, definirTexto] = useState(atual.texto);
  const [erro, definirErro] = useState<string | null>(null);
  const [salvo, definirSalvo] = useState(false);

  /*
   * O servidor é a verdade: quando a listagem recarrega, o rascunho não
   * sobrevive a um texto novo vindo de lá.
   *
   * Sem isto, duas abas abertas na mesma revenda mostrariam textos diferentes,
   * e a segunda a salvar desfaria a primeira sem avisar.
   */
  useEffect(() => {
    definirTexto(atual.texto);
    definirSalvo(false);
  }, [atual.texto]);

  const salvar = useMutation({
    mutationFn: async (mensagem: MensagemAutomatica) => {
      definirErro(null);
      definirSalvo(false);
      /*
       * Só o bloco mexido vai no corpo.
       *
       * Mandar os dois a cada salvamento apagaria a mensagem do outro com o
       * que esta tela tivesse em memória — e ela pode estar velha. Ausente
       * significa "não mexa", e é isso que impede o salvamento de uma seção de
       * desfazer a outra.
       */
      await api!.alterarMensagensAutomaticas({
        caminho: { id: canal.referencia_externa },
        corpo: { [qual]: mensagem },
      });
    },
    onSuccess: () => {
      definirSalvo(true);
      void cache.invalidateQueries({ queryKey: chaves.canaisDeWhatsApp() });
    },
    onError: (falha: unknown) => {
      definirErro(recadoDeErro(falha, { generica: 'Não foi possível salvar a mensagem.' }));
    },
  });

  const limpo = texto.trim();
  const mudou = texto !== atual.texto;

  function guardar(ligada: boolean) {
    /*
     * Ligada com texto vazio é o pior estado desta tela: o botão fica verde e
     * o cliente não recebe nada. O motor aceita — ele não exige texto —, então
     * a recusa tem de ser daqui.
     */
    if (ligada && limpo === '') {
      definirErro('Escreva a mensagem antes de ligar — ligada e vazia, ela não sai.');
      return;
    }
    if (limpo.length > MAXIMO) {
      definirErro(`A mensagem passa do limite de ${MAXIMO.toLocaleString('pt-BR')} caracteres.`);
      return;
    }
    salvar.mutate({ ligada, texto: limpo });
  }

  const ocupado = salvar.isPending || api === null;
  /*
   * `Botao` exige o motivo junto com `desabilitado`, e é uma boa exigência: um
   * botão cinza sem explicação é a pergunta mais comum do suporte.
   */
  const porQueOcupado =
    api === null ? 'Sem conexão agora. Tente de novo em instantes.' : 'Salvando…';

  return (
    <section className="auto__bloco" aria-label={titulo}>
      <div className="auto__topo">
        <h4 className="auto__titulo">{titulo}</h4>
        <Selo tom={atual.ligada ? 'ok' : 'neutro'} role="status">
          {atual.ligada ? 'Ligada' : 'Desligada'}
        </Selo>
      </div>

      <p className="auto__explicacao">{explicacao}</p>

      <CampoArea
        rotulo={`Texto da ${titulo.toLowerCase()}`}
        rotuloVersal
        rows={3}
        maxLength={MAXIMO}
        placeholder={exemplo}
        value={texto}
        onChange={(evento) => {
          definirTexto(evento.target.value);
          definirSalvo(false);
        }}
        ajuda={
          atual.ligada
            ? 'Está no ar: sai para quem escrever para este número.'
            : 'Salvar o texto não liga a mensagem — ela só passa a sair quando você ligar.'
        }
      />

      <div className="auto__acoes">
        {atual.ligada ? (
          <>
            <Botao
              miudo
              desabilitado={ocupado || !mudou}
              motivo={
                api === null
                  ? 'Sem conexão agora. Tente de novo em instantes.'
                  : 'Nada mudou no texto.'
              }
              onClick={() => guardar(true)}
            >
              {salvar.isPending ? 'Salvando…' : 'Salvar texto'}
            </Botao>
            <Botao
              miudo
              variante="secundario"
              desabilitado={ocupado}
              motivo={porQueOcupado}
              onClick={() => salvar.mutate({ ligada: false, texto: limpo })}
            >
              Desligar
            </Botao>
          </>
        ) : (
          <Botao miudo desabilitado={ocupado} motivo={porQueOcupado} onClick={() => guardar(true)}>
            {salvar.isPending ? 'Ligando…' : 'Ligar'}
          </Botao>
        )}
      </div>

      {/*
        `role="status"` porque a confirmação chega depois de uma ação que não
        muda a página: sem isso, quem usa leitor de tela não fica sabendo que
        salvou.
      */}
      {salvo && !erro && (
        <p className="auto__aviso auto__aviso--ok" role="status">
          Salvo.{' '}
          {qual === 'fora_de_expediente' && atual.ligada
            ? 'O horário usado é o de Configurações → Atendimento.'
            : ''}
        </p>
      )}
      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
    </section>
  );
}

/** O resumo que a aba mostra ao lado do nome, sem abrir: "saudação no ar". */
export function resumoDasMensagensAutomaticas(
  mensagens: CanalDeWhatsApp['mensagens_automaticas'],
): string {
  const ligadas = [
    mensagens.saudacao.ligada ? 'saudação' : null,
    mensagens.fora_de_expediente.ligada ? 'fora de expediente' : null,
  ].filter(Boolean);

  if (ligadas.length === 0) return 'nenhuma ligada';
  return `${ligadas.join(' e ')} no ar`;
}
